import { useState } from 'react';
import { cx } from '@/utils';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import {
  $getRoot,
  createEditor,
  LexicalEditor as Editor,
  EditorState,
  SerializedEditorState,
} from 'lexical';

import { BaseLexicalComponent, BaseLexicalProps } from './BaseLexicalComponent';
import { AutoLinkPlugin } from './plugins/AutoLink';
import { EditLinkPlugin } from './plugins/EditLink';
import { FloatingMenuPlugin } from './plugins/FloatingMenu';
import { LocalStoragePlugin } from './plugins/LocalStorage';
import { MaxLengthPlugin } from './plugins/MaxLength';
import { MyOnChangePlugin } from './plugins/MyOnChangePlugin';
import { ValueUpdatePlugin } from './plugins/ValueUpdatePlugin';
import { EDITOR_NAMESPACE } from './SharedLexicalConfig';

export type LexicalEditorProps = BaseLexicalProps & {
  value?: string | SerializedEditorState;
  callbackRoot?: (editorState: EditorState) => void;
  showLength?: boolean;
  maxLength?: number;
};

export function LexicalEditor({ config, ...props }: LexicalEditorProps) {
  const [textLength, setTextLength] = useState(0);
  const isMaxLengthReached = props.maxLength !== undefined && textLength > props.maxLength;
  function onChange(editorState: EditorState) {
    editorState.read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      setTextLength(textContent.length);
    });
    props.callbackRoot?.(editorState);
  }

  return (
    <BaseLexicalComponent
      placeholder="test"
      contentEditableProps={{
        className: cx(
          'relative h-auto resize-y rounded border-2 p-4 focus:outline-none focus-visible:border-black',
          isMaxLengthReached
            ? 'border-red-500 focus-visible:border-red-500'
            : 'border-black focus-visible:border-black'
        ),
      }}
      config={config}
      {...props}
    >
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <AutoLinkPlugin />
      <EditLinkPlugin />
      <FloatingMenuPlugin />
      <LocalStoragePlugin namespace={EDITOR_NAMESPACE} />
      {props.maxLength && <MaxLengthPlugin maxLength={props.maxLength} />}
      <MyOnChangePlugin onChange={onChange} maxLength={props.maxLength} />
      <ValueUpdatePlugin value={props.value} />
    </BaseLexicalComponent>
  );
}

export function getPlainTextFromEditorState(serializedEditorState: string): string {
  const editor: Editor = createEditor();
  const parsedEditorState: EditorState = editor.parseEditorState(serializedEditorState);

  const plainText: string = parsedEditorState.read(() => $getRoot().getTextContent());

  return plainText;
}
